import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import NECSLogo from '../components/NECSLogo';
import { toast } from 'sonner';
import NECSAccessRequestForm from '../components/NECSAccessRequestForm';
import TenantRegisterForm from '../components/TenantRegisterForm';
import SmsConsentCheckbox, { phoneRequiresConsent } from '../components/SmsConsentCheckbox';
import PasswordField from '../components/PasswordField';
import '../styles/public-page-titles.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const packages = [
  "Essential Collection",
  "Professional Collection",
  "Elite Collection",
  "Signature Collection",
  "Noble Empire Executive Collection",
  "Noble Empire Presidential Collection",
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roleParam = searchParams.get('role');
  // necs_staff | necs_admin → company-side access request form (lead capture)
  // property_admin (or no param) → property manager demo-request form (existing)
  const isCompanySide = roleParam === 'necs_staff' || roleParam === 'necs_admin';
  const isTenantSide = roleParam === 'tenant_resident';

  const { setUser } = useAuth();
  // Iter-108.3 · Platform-wide password polish state.
  const [revealBoth, setRevealBoth] = useState(false);
  const [pastedNotice, setPastedNotice] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirm_password: '',
    name: '',
    phone: '',
    role: 'property_manager',
    unit_number: '',
    building_number: '',
    property_name: '',
    address: '',
    total_units: '',
    total_buildings: '',
    package_type: '',
    property_plan: '',
    residents_file: '',
    residents_preview: [],
  });
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [uploadingPlan, setUploadingPlan] = useState(false);
  const [uploadingResidents, setUploadingResidents] = useState(false);
  // A2P 10DLC compliance — user must actively opt in to SMS before we can
  // send Twilio-initiated texts to the phone they provided.
  const [smsConsent, setSmsConsent] = useState(false);
  const isPropertyManagerOnboarding = true;

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // After Google auth, AuthCallback intercepts the #session_id hash and routes by role.
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Detect in-app webviews (Emergent preview, Facebook, Instagram, etc.) — Google blocks OAuth there
  const isEmbeddedWebView = (() => {
    if (typeof navigator === 'undefined') return false;
    const ua = (navigator.userAgent || '').toLowerCase();
    return /(wv|fban|fbav|instagram|line|; webview|emergent)/.test(ua);
  })();

  useEffect(() => {
    if (isPropertyManagerOnboarding) {
      setFormData((prev) => ({ ...prev, role: 'property_manager' }));
    }
  }, [isPropertyManagerOnboarding]);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/properties`);
      if (response.ok) {
        const data = await response.json();
        setProperties(data);
      }
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingPlan(true);
    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setFormData({...formData, property_plan: base64String});
        toast.success('Property plan uploaded');
        setUploadingPlan(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload file');
      setUploadingPlan(false);
    }
  };

  const handleResidentsUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingResidents(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const dataUrl = reader.result;
        const b64 = String(dataUrl).split(',', 2)[1] || '';
        // Parse the file server-side so we can preview the extracted rows to the user.
        try {
          const res = await fetch(`${BACKEND_URL}/api/qr/residents/parse`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ file_b64: b64 })
          });
          if (res.ok) {
            const data = await res.json();
            setFormData((prev) => ({
              ...prev,
              residents_file: dataUrl,
              residents_preview: data.residents || [],
            }));
            toast.success(`Loaded ${data.parsed || 0} resident row(s)`);
          } else {
            // Auth required for /parse — still attach the file so admin can process it later.
            setFormData((prev) => ({ ...prev, residents_file: dataUrl, residents_preview: [] }));
            toast.info('Residents list attached. It will be processed after your account is approved.');
          }
        } catch (_e) {
          setFormData((prev) => ({ ...prev, residents_file: dataUrl, residents_preview: [] }));
          toast.info('Residents list attached.');
        }
        setUploadingResidents(false);
      };
      reader.readAsDataURL(file);
    } catch (_e) {
      toast.error('Failed to read residents file');
      setUploadingResidents(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirm_password) {
      toast.error('Passwords do not match. Please confirm your password.');
      return;
    }

    // A2P 10DLC — if a phone number was provided, SMS consent is mandatory.
    if (phoneRequiresConsent(formData.phone, smsConsent)) {
      toast.error('Please agree to receive SMS messages before continuing, or clear the phone number.');
      return;
    }

    setLoading(true);

    try {
      // Don't send confirm_password or the raw file base64 to the backend.
      const { confirm_password, residents_file, residents_preview, initial_building_codes_raw, ...rest } = formData;

      // Parse "bldg=code" lines into a dict — accepts eg. "1=4455" or "A: 8801".
      const initial_building_codes = {};
      String(initial_building_codes_raw || '')
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .forEach((line) => {
          const m = line.match(/^\s*(\S+?)\s*[:=]\s*(.+?)\s*$/);
          if (m) initial_building_codes[m[1]] = m[2];
        });

      const payload = {
        ...rest,
        initial_building_codes: Object.keys(initial_building_codes).length ? initial_building_codes : undefined,
        residents_file_b64: residents_file || undefined,
        residents_preview: residents_preview?.length ? residents_preview : undefined,
      };
      const response = await fetch(`${BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Registration failed');
      }

      const data = await response.json();

      // 🔒 Property-manager path now returns a "lead_received" response
      // (no auto-login). Show a thank-you state and redirect back home.
      if (data && data.lead_received) {
        toast.success(data.message || 'Thanks — our team will reach out within one business day.');
        setTimeout(() => navigate('/'), 1500);
        return;
      }

      setUser(data.user);
      toast.success('Account created successfully!');
      
      // Redirect based on role
      const staffRoles = ['supervisor', 'crew_leader', 'agent'];
      if (staffRoles.includes(data.user.role)) {
        navigate('/staff-dashboard', { state: { user: data.user } });
      } else if (data.user.role === 'property_manager') {
        navigate('/property-manager-dashboard', { state: { user: data.user } });
      } else if (data.user.role === 'company_manager') {
        navigate('/company-manager-dashboard', { state: { user: data.user } });
      } else {
        navigate('/tenant-dashboard', { state: { user: data.user } });
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const propertyAdminBg = !isCompanySide && !isTenantSide
    ? {
        backgroundImage:
          "linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.70)), url('https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/10g452i0_1597.jpg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        backgroundRepeat: 'no-repeat',
      }
    : undefined;

  // NECS Staff registration background — team services poster fixed behind the form.
  const necsStaffBg = roleParam === 'necs_staff'
    ? {
        backgroundImage:
          "linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.70)), url('https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/9cvdccqx_2172.png')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        backgroundRepeat: 'no-repeat',
      }
    : undefined;

  // NECS Admin registration background — boardroom service-overview shot fixed behind the form.
  const necsAdminBg = roleParam === 'necs_admin'
    ? {
        backgroundImage:
          "linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.70)), url('https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/2pojcawf_2171.png')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        backgroundRepeat: 'no-repeat',
      }
    : undefined;

  const wrapperStyle = propertyAdminBg || necsStaffBg || necsAdminBg || undefined;
  const wrapperNeedsLightBg = (isCompanySide && !necsStaffBg && !necsAdminBg) || isTenantSide;

  // Tenant Registration background (iter-84) — both NECS resident scenes are
  // stitched together as a single connected background. Image A (mother
  // placing bag) fills the LEFT half, image B (senior at door 2408) fills
  // the RIGHT half, with a dark overlay so the form floating on top stays
  // readable. Same treatment other registration pages use, but split so both
  // pictures show clearly on their own side.
  const TENANT_IMG_LEFT  = 'https://customer-assets-eiarnc6j.emergentagent.net/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/y7om79r3_1925.png';
  const TENANT_IMG_RIGHT = 'https://customer-assets-eiarnc6j.emergentagent.net/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/6x8djp2z_1924.png';
  const tenantBg = isTenantSide
    ? {
        backgroundImage: [
          'linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.72))',
          `url('${TENANT_IMG_LEFT}')`,
          `url('${TENANT_IMG_RIGHT}')`,
        ].join(', '),
        backgroundPosition: 'center, left center, right center',
        backgroundSize:     'cover, 50% 100%, 50% 100%',
        backgroundRepeat:   'no-repeat, no-repeat, no-repeat',
        backgroundAttachment: 'fixed, fixed, fixed',
        backgroundColor: '#0a0a0a',
      }
    : undefined;

  const finalWrapperStyle = tenantBg || wrapperStyle;

  return (
    <div
      className={`min-h-screen flex items-center justify-center ${isTenantSide ? '' : 'p-4'} ${wrapperNeedsLightBg && !tenantBg ? 'bg-zinc-50' : ''}`}
      style={finalWrapperStyle}
      data-testid="register-page-wrapper"
    >
      {isCompanySide ? (
        <NECSAccessRequestForm kind={roleParam} />
      ) : isTenantSide ? (
        <div
          className="w-full min-h-screen flex items-center justify-center py-6 px-3 sm:py-10 sm:px-4"
          data-testid="tenant-register-layout"
        >
          {/* iter-84 · The two NECS resident photos are now stitched into the
              page-wide fixed background (see tenantBg above) so each side
              shows clearly and the form floats over both. The old flanking
              columns are dropped to remove duplicate rendering. */}
          <div
            className="hidden lg:block w-40 xl:w-56 flex-shrink-0"
            aria-hidden="true"
            data-testid="tenant-register-image-left"
          />
          <div className="flex-shrink-0 w-full max-w-2xl flex items-start lg:items-center justify-center min-w-0">
            <TenantRegisterForm />
          </div>
          <div
            className="hidden lg:block w-40 xl:w-56 flex-shrink-0"
            aria-hidden="true"
            data-testid="tenant-register-image-right"
          />
        </div>
      ) : (
      <Card
        className="w-full max-w-2xl p-8 shadow-2xl"
        data-testid="register-form"
      >
        <div className="flex justify-center mb-4">
          <NECSLogo variant="header" size="sm" dataTestId="register-page-logo" />
        </div>

        <h2 className="necs-web-page-title text-center mb-2" data-testid="register-page-title">Request a Demo</h2>
        <p className="text-center text-sm text-zinc-600 mb-6">For property managers in the Dallas–Fort Worth area</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                data-testid="name-input"
                className="focus-visible:ring-[#FFD60A]"
              />
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                data-testid="email-input"
                className="focus-visible:ring-[#FFD60A]"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <PasswordField
              label="Password"
              hint="(min 6 chars)"
              required
              minLength={6}
              value={formData.password}
              onChange={(v) => setFormData({ ...formData, password: v })}
              autoComplete="new-password"
              showStrength
              onPasteWarning={setPastedNotice}
              reveal={revealBoth}
              onToggleReveal={() => setRevealBoth((v) => !v)}
              testId="password-input"
            />
            <PasswordField
              label="Confirm Password"
              required
              minLength={6}
              value={formData.confirm_password}
              onChange={(v) => setFormData({ ...formData, confirm_password: v })}
              autoComplete="new-password"
              matchValue={formData.password}
              onPasteWarning={setPastedNotice}
              reveal={revealBoth}
              onToggleReveal={() => setRevealBoth((v) => !v)}
              testId="confirm-password-input"
            />
            {pastedNotice && (
              <p
                className="md:col-span-2 text-[10px] font-bold px-2 py-1.5 rounded"
                style={{ backgroundColor: 'rgba(239,68,68,0.15)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.4)' }}
                data-testid="register-page-paste-guard"
              >
                ⚠️ You pasted content that looks like an email or a company name. Passwords should never contain your identity.
              </p>
            )}

            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="+1234567890"
                data-testid="phone-input"
                className="focus-visible:ring-[#FFD60A]"
              />
            </div>
          </div>

          {!isPropertyManagerOnboarding && (
            <div>
              <Label htmlFor="role">Account Type</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                <SelectTrigger data-testid="role-select" className="focus:ring-[#FFD60A]">
                  <SelectValue placeholder="Select account type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tenant" data-testid="role-option-tenant">Tenant/Resident</SelectItem>
                  <SelectItem value="supervisor" data-testid="role-option-supervisor">Supervisor (Driver)</SelectItem>
                  <SelectItem value="crew_leader" data-testid="role-option-crew-leader">Crew Leader (Driver)</SelectItem>
                  <SelectItem value="agent" data-testid="role-option-agent">Agent (Staff)</SelectItem>
                  <SelectItem value="property_manager" data-testid="role-option-manager">Property Manager</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500 mt-1">
                {formData.role === 'supervisor' && 'Supervisors operate vehicles and manage crews'}
                {formData.role === 'crew_leader' && 'Crew Leaders operate vehicles and lead field teams'}
                {formData.role === 'agent' && 'Agents are field staff who collect trash'}
              </p>
            </div>
          )}

          {isPropertyManagerOnboarding && (
            <div className="rounded-lg border border-[#C9A962]/40 bg-amber-50/60 p-4" data-testid="property-manager-onboarding-banner">
              <p className="text-sm font-bold text-[#2D2D2D]">Property Manager Demo Request</p>
              <p className="text-xs text-zinc-700 mt-1 leading-relaxed">
                This form is for property managers only. Tell us about your property and our team will reach out within one business day to schedule your demo, finalize pricing, and onboard your account.
              </p>
              <p className="text-xs text-zinc-600 mt-2">
                Looking to add a <strong>tenant</strong>, <strong>staff member</strong>, or <strong>supervisor</strong>? Those accounts are created by your administrator from inside the dashboard.
              </p>
            </div>
          )}

          {formData.role === 'property_manager' && (
            <>
              <div>
                <Label htmlFor="property_name">Property Name</Label>
                <Input
                  id="property_name"
                  value={formData.property_name}
                  onChange={(e) => setFormData({...formData, property_name: e.target.value})}
                  required
                  data-testid="property-input"
                  className="focus-visible:ring-[#FFD60A]"
                  placeholder="e.g., Oak Creek Apartments"
                />
              </div>

              <div>
                <Label htmlFor="address">Property Address (Location)</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  required
                  data-testid="address-input"
                  className="focus-visible:ring-[#FFD60A]"
                  placeholder="123 Main St, Dallas, TX 75001"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="total_units">Total Units</Label>
                  <Input
                    id="total_units"
                    type="number"
                    value={formData.total_units}
                    onChange={(e) => setFormData({...formData, total_units: e.target.value})}
                    required
                    data-testid="total-units-input"
                    className="focus-visible:ring-[#FFD60A]"
                    placeholder="e.g., 150"
                  />
                </div>

                <div>
                  <Label htmlFor="total_buildings">Total Buildings</Label>
                  <Input
                    id="total_buildings"
                    type="number"
                    value={formData.total_buildings}
                    onChange={(e) => setFormData({...formData, total_buildings: e.target.value})}
                    required
                    data-testid="total-buildings-input"
                    className="focus-visible:ring-[#FFD60A]"
                    placeholder="e.g., 8"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="property_plan">Property Plan / Floor Plan (Optional)</Label>
                  <Input
                    id="property_plan"
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleFileUpload}
                    data-testid="property-plan-input"
                    className="focus-visible:ring-[#FFD60A]"
                    disabled={uploadingPlan}
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    Upload floor plan or property layout to help drivers navigate the property
                  </p>
                  {uploadingPlan && <p className="text-xs text-[#FFD60A] mt-1">Uploading...</p>}
                  {formData.property_plan && <p className="text-xs text-green-600 mt-1">✓ Plan uploaded</p>}
                </div>

                <div>
                  <Label htmlFor="residents_file">Upload Residents Info List (Optional)</Label>
                  <Input
                    id="residents_file"
                    type="file"
                    accept=".csv,.xls,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={handleResidentsUpload}
                    data-testid="residents-file-input"
                    className="focus-visible:ring-[#FFD60A]"
                    disabled={uploadingResidents}
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    CSV or XLSX with columns: <strong>Name, Unit Number, Phone, Email</strong>. Each unit QR will be labelled with the resident&apos;s name so notifications are accurate.
                  </p>
                  {uploadingResidents && <p className="text-xs text-[#FFD60A] mt-1">Reading file...</p>}
                  {formData.residents_preview?.length > 0 && (
                    <p className="text-xs text-green-600 mt-1" data-testid="residents-preview-count">
                      ✓ {formData.residents_preview.length} resident(s) detected — will be imported on approval
                    </p>
                  )}
                  {formData.residents_file && formData.residents_preview?.length === 0 && (
                    <p className="text-xs text-amber-500 mt-1">
                      ✓ File attached — our team will process it during onboarding
                    </p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="package_type">Select Package for Property</Label>
                <Select value={formData.package_type} onValueChange={(value) => setFormData({...formData, package_type: value})}>
                  <SelectTrigger data-testid="package-select" className="focus:ring-[#FFD60A]">
                    <SelectValue placeholder="Choose a package" />
                  </SelectTrigger>
                  <SelectContent>
                    {packages.map(pkg => (
                      <SelectItem key={pkg} value={pkg}>{pkg}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Nationwide Scalability Masterplan — Initial access codes */}
              <div className="pt-2 border-t border-zinc-200">
                <p className="text-sm font-semibold text-zinc-800 mb-1">Initial Access Codes (Optional)</p>
                <p className="text-xs text-zinc-500 mb-3">
                  So our crew can start day one without waiting for a call-in. You can rotate these later from your Property Manager dashboard.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="initial_gate_code">Initial Gate Code</Label>
                    <Input
                      id="initial_gate_code"
                      value={formData.initial_gate_code || ''}
                      onChange={(e) => setFormData({...formData, initial_gate_code: e.target.value})}
                      data-testid="initial-gate-code-input"
                      className="focus-visible:ring-[#FFD60A]"
                      placeholder="e.g., 1234#"
                    />
                  </div>
                  <div>
                    <Label htmlFor="initial_building_codes">Building Codes (per line: bldg=code)</Label>
                    <Textarea
                      id="initial_building_codes"
                      value={formData.initial_building_codes_raw || ''}
                      onChange={(e) => setFormData({...formData, initial_building_codes_raw: e.target.value})}
                      data-testid="initial-building-codes-input"
                      className="focus-visible:ring-[#FFD60A] text-sm font-mono"
                      placeholder={'1=4455\nA=8801\nB=2020'}
                      rows={4}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {formData.role === 'tenant' && (
            <>
              <div>
                <Label htmlFor="property_name">Select Property</Label>
                <Select value={formData.property_name} onValueChange={(value) => setFormData({...formData, property_name: value})}>
                  <SelectTrigger data-testid="property-select" className="focus:ring-[#FFD60A]">
                    <SelectValue placeholder="Choose your property" />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map(prop => (
                      <SelectItem key={prop.property_id} value={prop.property_name}>
                        {prop.property_name} - {prop.package_type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {properties.length === 0 && (
                  <p className="text-sm text-zinc-500 mt-1">No properties available. Property manager needs to register first.</p>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="building_number">Building Number</Label>
                  <Input
                    id="building_number"
                    value={formData.building_number}
                    onChange={(e) => setFormData({...formData, building_number: e.target.value})}
                    data-testid="building-input"
                    className="focus-visible:ring-[#FFD60A]"
                    placeholder="e.g., Building A or 1"
                  />
                </div>

                <div>
                  <Label htmlFor="unit_number">Unit Number</Label>
                  <Input
                    id="unit_number"
                    value={formData.unit_number}
                    onChange={(e) => setFormData({...formData, unit_number: e.target.value})}
                    data-testid="unit-input"
                    className="focus-visible:ring-[#FFD60A]"
                    placeholder="e.g., 101"
                  />
                </div>
              </div>
            </>
          )}

          <SmsConsentCheckbox
            checked={smsConsent}
            onChange={setSmsConsent}
            testid="register-sms-consent"
          />

          <Button 
            type="submit" 
            className="w-full bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold"
            disabled={loading || phoneRequiresConsent(formData.phone, smsConsent)}
            data-testid="register-submit-button"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </Button>

          <p className="text-center text-xs text-zinc-500 mt-3" data-testid="register-legal-links">
            By creating an account, you agree to our{' '}
            <Link to="/terms" className="text-[#FFD60A] hover:underline" data-testid="register-terms-link">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-[#FFD60A] hover:underline" data-testid="register-privacy-link">Privacy Policy</Link>.
          </p>
        </form>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-zinc-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-zinc-500">Or continue with</span>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={handleGoogleSignup}
          data-testid="google-signup-button"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </Button>

        {isEmbeddedWebView && (
          <div
            className="mt-3 p-3 rounded-lg border text-xs leading-relaxed"
            style={{ backgroundColor: '#FFFCEC', borderColor: '#FFD60A', color: '#3D3D3D' }}
            data-testid="embedded-webview-notice"
          >
            <p className="font-bold mb-1">Heads up — Google may block sign-in here.</p>
            <p>
              You appear to be inside an in-app browser. Google's security policy blocks OAuth in
              embedded webviews. Open this page in Safari or Chrome to use Google sign-up — or fill
              out the form above.
            </p>
          </div>
        )}

        <p className="text-center text-sm text-zinc-600 mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-[#FFD60A] hover:underline font-semibold" data-testid="login-link">
            Login here
          </Link>
        </p>

        <p className="text-center text-sm text-zinc-600 mt-4">
          <Link to="/login" className="text-[#FFD60A] hover:underline font-semibold" data-testid="back-prev-link">
            ← Back to Previous Page
          </Link>
        </p>
      </Card>
      )}
    </div>
  );
}